import React, { useCallback } from 'react';
import {
  calculateRankIndicatorPositions,
  ConversionRateZonedHighlight,
  RankIndicator,
} from '../../Components';
import { useOverlayContainer } from '../hooks';
import { OverlayHighlightDetails } from '../../types';
import { ConversionOverlayDisplayOptions, elemIdIdent } from '../../ClickMaps';
import { ConversionOverlayTooltipContent } from './ConversionOverlayTooltipContent';
import { SimpleRect } from '@fullstory/util/rect';

/**
 * The minimum size to show the percent overlay text. Both the height and the width of this element must be greater than
 * or equal to show the percent on the overlay.
 */
const minTextBounds = {
  height: 32, // font is 36px but displays around 24px, if we ignore the space around it
  width: 115,
};

export interface ConversionOverlayContainerProps {
  /** The playback scale factor (i.e. zoomInverseScale * (frameInverseScale / visualInverseScale) */
  playbackScaleFactor: number;
  /** On rank indicator or zone select */
  onSelect: (rank: number) => void;
  inverseScale: number;
  selectedRank: number;
  clickOverlayDetails: OverlayHighlightDetails[];
  displayOptions: ConversionOverlayDisplayOptions;
  layoutViewport: SimpleRect;
  conversionEventLabel: string;
}

export const ConversionOverlayContainer: React.FC<ConversionOverlayContainerProps> = ({
  playbackScaleFactor,
  clickOverlayDetails,
  onSelect,
  selectedRank,
  inverseScale,
  displayOptions,
  layoutViewport,
  conversionEventLabel,
}) => {
  const { hoveringItem, overlayRefs, allOverlayAwareOnHover, onHover } = useOverlayContainer();

  // Reverse since we want lowered number rank indicators (more important ones) above higher numbered ones
  const orderedClientRects = [...clickOverlayDetails];
  orderedClientRects.reverse();

  const rankIndicatorPositions = calculateRankIndicatorPositions(orderedClientRects, inverseScale);

  const shouldHideText = useCallback(
    (rank: number) => {
      const rect = overlayRefs.current[rank - 1]?.getBoundingClientRect();
      if (!rect) {
        return false;
      }

      return (
        rect.width * inverseScale < minTextBounds.width ||
        rect.height * inverseScale < minTextBounds.height
      );
    },
    [overlayRefs.current, inverseScale],
  );

  return (
    <>
      {orderedClientRects.map((overlayHighlightDetails, index) => {
        const elemIdent = elemIdIdent(
          overlayHighlightDetails.elementStats.NamedElementId,
          overlayHighlightDetails.elementStats.PropertyValue,
        );
        const rank = overlayHighlightDetails.rank;
        const conversionRate =
          displayOptions?.elementConversionStats?.elementStats[elemIdent]?.conversionRate || 0;

        const tooltip = (
          <ConversionOverlayTooltipContent
            elementStats={displayOptions?.elementConversionStats?.elementStats[elemIdent]}
            conversionEventLabel={conversionEventLabel}
          />
        );

        return (
          <React.Fragment key={rank}>
            <ConversionRateZonedHighlight
              clickOverlayDetail={overlayHighlightDetails}
              conversionRate={conversionRate}
              hovering={hoveringItem === rank}
              onHover={allOverlayAwareOnHover}
              key={`${rank}-highlight`}
              playbackScaleFactor={playbackScaleFactor}
              tooltipContent={tooltip}
              onSelect={onSelect}
              selected={selectedRank === rank}
              hideText={shouldHideText(rank)}
              ref={element => (overlayRefs.current[rank - 1] = element!)}
              zIndex={orderedClientRects.length - rank}
              colorClass={overlayHighlightDetails.color}
            />
            <RankIndicator
              rank={rank}
              selected={selectedRank === rank}
              onSelect={() => onSelect(hoveringItem)}
              playbackScaleFactor={playbackScaleFactor}
              clientRect={rankIndicatorPositions[index]!}
              key={`${rank}-rankindicator`}
              hovering={hoveringItem === rank}
              setHovering={onHover}
              zIndex={orderedClientRects.length + orderedClientRects.length - rank}
              layoutViewport={layoutViewport}
              styleOverrides={displayOptions.rankIndicatorStyleOverrides}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};
